import {
  Button,
  FormInput,
  FormNumber,
  FormRoot,
  FormSelect,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import {
  paymentTermsHtmlToNullable,
  toPaymentTermsEditorHtml,
} from "@/shared/lib/performance-document-payment-terms";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import { SimpleRichTextEditor } from "@/shared/ui/SimpleRichTextEditor";
import type {
  BankAccountTaxVariantDTO,
  CreateBankAccountTemplateDTO,
  UpdateBankAccountTemplateDTO,
} from "../../domain/contracts";

const BankAccountTemplateFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງປ້ອນຊື່ແມ່ແບບ"),
  taxVariant: z.enum(["without_tax", "with_tax"]),
  content: z.string().min(1, "ຕ້ອງປ້ອນເນື້ອຫາ"),
  contentEn: z.string(),
  status: z.enum(["active", "inactive"]),
  sortOrder: z.number().int(),
  isDefault: z.boolean(),
});

type BankAccountTemplateFormValues = z.infer<
  typeof BankAccountTemplateFormSchema
>;

const TAX_VARIANT_OPTIONS: {
  value: BankAccountTaxVariantDTO;
  label: string;
}[] = [
  { value: "without_tax", label: "ເອກະສານບໍ່ມີພາສີ" },
  { value: "with_tax", label: "ເອກະສານມີພາສີ" },
];

type BankAccountTemplateFormProps = {
  initialValues?: Partial<BankAccountTemplateFormValues>;
  onSubmit: (
    values: CreateBankAccountTemplateDTO | UpdateBankAccountTemplateDTO,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
};

export function BankAccountTemplateForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
}: BankAccountTemplateFormProps) {
  const methods = RHF.useForm<BankAccountTemplateFormValues>({
    resolver: zodResolver(BankAccountTemplateFormSchema),
    defaultValues: {
      name: initialValues?.name ?? "",
      taxVariant: initialValues?.taxVariant ?? "without_tax",
      content: initialValues?.content ?? "",
      contentEn: initialValues?.contentEn ?? "",
      status: initialValues?.status ?? "active",
      sortOrder: initialValues?.sortOrder ?? 0,
      isDefault: initialValues?.isDefault ?? false,
    },
  });

  return (
    <FormRoot
      methods={methods}
      onSubmit={(vals) => {
        onSubmit({
          name: vals.name,
          taxVariant: vals.taxVariant,
          content: paymentTermsHtmlToNullable(vals.content) ?? vals.content,
          contentEn: paymentTermsHtmlToNullable(vals.contentEn),
          status: vals.status,
          sortOrder: vals.sortOrder,
          isDefault: vals.isDefault,
        });
      }}
      className="space-y-4 py-6"
    >
      <FormInput name="name" label="ຊື່ແມ່ແບບ" required />
      <FormSelect
        name="taxVariant"
        label="ໃຊ້ກັບສະຖານະພາສີ"
        options={TAX_VARIANT_OPTIONS.map((item) => ({
          value: item.value,
          label: item.label,
        }))}
      />
      <div className="space-y-1.5">
        <label className="font-medium text-sm">
          ຂໍ້ມູນບັນຊີທະນາຄານ (ລາວ) <span className="text-destructive">*</span>
        </label>
        <SimpleRichTextEditor
          resetKey={`bank-account-template-lo:${initialValues?.name ?? "new"}`}
          initialHtml={toPaymentTermsEditorHtml(methods.watch("content"))}
          onHtmlChange={(html) =>
            methods.setValue(
              "content",
              paymentTermsHtmlToNullable(html) ?? "",
              { shouldDirty: true, shouldValidate: true },
            )
          }
          placeholder="ຂຽນຄຳອະທິບາຍບລັອກນີ້…"
          minHeightClassName="min-h-[180px]"
        />
        {methods.formState.errors.content ? (
          <p className="text-destructive text-xs">
            {methods.formState.errors.content.message}
          </p>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <label className="font-medium text-sm">Bank account (English)</label>
        <SimpleRichTextEditor
          resetKey={`bank-account-template-en:${initialValues?.name ?? "new"}`}
          initialHtml={toPaymentTermsEditorHtml(methods.watch("contentEn"))}
          onHtmlChange={(html) =>
            methods.setValue(
              "contentEn",
              paymentTermsHtmlToNullable(html) ?? "",
              { shouldDirty: true },
            )
          }
          placeholder="Write bank account details in English…"
          minHeightClassName="min-h-[160px]"
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Rich text editor — ຕັ້ງແມ່ແບບເລີ່ມຕົ້ນແຍກສຳລັບມີພາສີ / ບໍ່ມີພາສີ · ພິມ EN ຈະໃຊ້ຂໍ້ຄວາມພາສາອັງກິດ (ຖ້າບໍ່ມີຈະ fallback ລາວ)
      </p>
      <div className="grid grid-cols-2 gap-4">
        <FormSelect
          name="status"
          label="ສະຖານະ"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
        <FormNumber name="sortOrder" label="ລຳດັບການສະແດງ" min={0} />
      </div>
      <FormCheckboxCard
        name="isDefault"
        label="ຕັ້ງເປັນແມ່ແບບເລີ່ມຕົ້ນ"
        description="ໃຊ້ເປັນຄ່າເລີ່ມຕົ້ນຕາມສະຖານະພາສີ"
      />
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
